// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { GLOSSARY } from '../glossary';
import { closeButton, openOverlay } from '../overlay';
import { esc } from '../format';

/**
 * One delegated listener for every ℹ trigger on the page, present or future.
 *
 * The popover is a real overlay — scrim, Escape, ✕, click-outside — rather than
 * a bare absolutely-positioned div, because a definition bubble that can only
 * be closed by clicking the exact same 20px button is unusable on a phone,
 * where a thumb covers the button.
 */
export function initGlossary(root: ParentNode = document): void {
  root.addEventListener('click', (e) => {
    const trigger = (e.target as HTMLElement | null)?.closest<HTMLElement>('.glossary-link');
    if (!trigger) return;
    const key = trigger.dataset.term;
    if (!key) return;
    const entry = GLOSSARY[key];
    if (!entry) return;
    e.preventDefault();
    openGlossary(entry.term, entry.definition, trigger);
  });
}

function openGlossary(term: string, definition: string, anchor: HTMLElement): void {
  const handle = openOverlay({
    panelClass: 'gloss-pop',
    label: `Definition: ${term}`,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.style.fontSize = 'var(--font-size-lg)';
      h.textContent = term;
      head.append(h, closeButton(close, `Close definition of ${term}`));
      const body = document.createElement('div');
      body.className = 'small';
      body.innerHTML = `<p>${esc(definition)}</p>`;
      panel.append(head, body);
    },
  });

  // Position near the trigger, then pull back inside the viewport. Done after
  // the panel is in the DOM so its measured height is real.
  const rect = anchor.getBoundingClientRect();
  const panel = handle.panel;
  const pr = panel.getBoundingClientRect();
  const margin = 8;
  let left = rect.left + rect.width / 2 - pr.width / 2;
  left = Math.max(margin, Math.min(left, innerWidth - pr.width - margin));
  let top = rect.bottom + margin;
  if (top + pr.height > innerHeight - margin) top = Math.max(margin, rect.top - pr.height - margin);
  panel.style.left = `${Math.round(left)}px`;
  panel.style.top = `${Math.round(top)}px`;
}
